/**
 * The PersonalizationUtils modulecontains utility APIs to manage the personalization instances of a list component.
 * @module PersonalizationUtils
 */
/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import type { AxiosResponse } from 'axios';

import { resolveRestApi } from '../router/app-router';
import { fetch as axiosFetch } from '../service_broker/fetch';
import { getPersonalizationMetadata } from '../rules/rules-store';
import type {
  RawPersonalization,
  PersonalizationStore,
  Personalizations,
  CreatePersonalizationInfo,
  PersonalizationInfo
} from '../rules/types';

const createListPersonalization = 'createListPersonalization';
const deleteListPersonalization = 'deleteListPersonalization';
const updateListPersonalization = 'updateListPersonalization';
const getUIListPersonalizations = 'getUIListPersonalizations';

// Todo ISSUE-77021: Output format has to be key/value pairs for better lookup
// DXAPI does not have support of pageGroup conversion to keyValue pair
// DXAPI was only supporting pageList… Probably DXAPi has to change
// and then in ideal world we do not require this transformation
/**
 * This function helps in transforming the personalization list structure to key value pairs.
 * @ignore
 * @param allPersonalization - list of personalizations
 */
function transformPersonalizationStructure(allPersonalization: RawPersonalization[] = []): Personalizations {
  return allPersonalization.reduce((all: Personalizations, personalization: RawPersonalization) => {
    all[personalization.personalizationID] = {
      name: personalization.name,
      personalizationState: JSON.parse(personalization.personalizationState || '{}')
    };
    return all;
  }, {});
}

const CREATE = 'create';
const UPDATE = 'update';
const DELETE = 'delete';
/**
 * @param action mutation action
 * @param listID A unique id referencing a list component.
 * @param personalizationID A unique id referencing the personalization instance.
 * @param personalizedState An object containing information about the personalization state.
 * @returns An object of list of personalization instances and default personalization for the list component.
 * @private
 */

function updatePersonalizationStore(
  action: string,
  listID: string,
  personalizationID: string,
  personalizedState?: CreatePersonalizationInfo
): object | null {
  const personalizations = getPersonalizationMetadata(listID);
  if (personalizations) {
    switch (action) {
      case CREATE:
      case UPDATE: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const { markAsDefault, ...restPersonalizedState } = personalizedState!;
        if (Object.entries(restPersonalizedState).length > 0) {
          /* When 'Set as default' action is performed then restPersonalizedState is empty. Hence added a check */
          (personalizations.allPersonalizations as Personalizations)[personalizationID] =
            restPersonalizedState as PersonalizationInfo;
        }
        if (markAsDefault) {
          personalizations.defaultPersonalization = personalizationID;
        }
        break;
      }
      case DELETE: {
        delete (personalizations.allPersonalizations as Personalizations)[personalizationID];
        if (personalizations.defaultPersonalization === personalizationID) {
          personalizations.defaultPersonalization = '';
        }
        break;
      }
      default:
        return null;
    }
    return personalizations;
  }
  return null;
}

// Todo ISSUE-77023: Temporary Hack and should be fine even if there are two tables on UI
// currently DXAPI is not taking ID from client to create Personalization
// which would probably change in future in DX API or table...
// As of now table behaves like a sync and not as a blocking operation while creating personalization
const tempPersonalizationKeyHack: { [key: string]: string } = {};
/**
 * This API retrieves the list of personalization instances for a list component and returns the list as a Promise.
 *
 * Note:The APIs response also contains a 'defaultPersonalization' flag which contains the default personalization ID.
 * If the default personalization ID does not exist,the 'defaultPersonalization' flag is empty.
 *
 * @param listID - A unique ID referencing a list component. The length of the ID should be limited to 32 characters.
 *
 * @example  <caption>In the example below, the API retrieves the list of personalization instances for the list component with ID 443533r555.</caption>
 *
 * const listId = "443533r555";
 * PCore.getPersonalizationUtils("443533r555").fetchPersonalizations();
 *
 * //response structure will be like below
 * {
 *    defaultPersonalization : "L_234322",
 *    allPersonalizations : [
 *      {
 *         "name" : "Open bugs",
 *         "personalizationId" : "L_345643",
 *         "personalizationState" : "{ filter : 'pyStatusWork = Open'}"
 *      },
 *      {
 *         "name" : "Resolved bugs",
 *         "personalizationId" : "L_234322",
 *         "personalizationState" : "{ filter : 'pyStatusWork STARTS_WITH Resolved'}"
 *      }
 *    ]
 * }
 *
 * @returns An object of list of personalization instances and default personalization for the list component.
 *
 * @function
 */
const fetchPersonalizations = async (
  listID: string
): Promise<AxiosResponse<PersonalizationStore> | PersonalizationStore> => {
  const personalizations = getPersonalizationMetadata(listID);
  if (personalizations) {
    if (Array.isArray(personalizations.allPersonalizations)) {
      personalizations.allPersonalizations = transformPersonalizationStructure(personalizations.allPersonalizations);
    }
    return Promise.resolve(personalizations);
  }

  const { restApi, reqType } = resolveRestApi(getUIListPersonalizations, {
    listID
  });

  return axiosFetch(restApi, {
    method: reqType,
    body: {}
  }).then((response: AxiosResponse<PersonalizationStore>) => {
    const { data } = response;
    const allPersonalizations = transformPersonalizationStructure(data.allPersonalizations as RawPersonalization[]);
    return {
      allPersonalizations,
      defaultPersonalization: data.defaultPersonalization
    };
  });
};

/**
 * This API creates a new personalization instance for a list component and returns the personalizationID as a Promise.
 * @param listID - A unique ID referencing a list component. The length of the ID should be limited to 32 characters.
 * @param personalizationID - A temporary unique ID which will be replaced by the actual ID returned by this Api.
 * @param personalizedState - An object containing information about the personalization state.
 *
 * @example  <caption>In the example below, the API creates personalization instance and returns a personalization ID.</caption>
 *
 * const listId = "443533r555";
 * const personalizationId = "L_343456";
 * const personalizedState = {
 *   "name" : "Open bugs",
 *   "markAsDefault" : true,
 *   "personalizationState" : "{ filter : 'pyStatusWork = Open'}"
 * };
 *
 * PCore.getPersonalizationUtils(listId).createPersonalization(personalizationId, personalizedState).then((response)=>{
 *    const personalizationId = response;
 * });
 *
 * @returns - the generated personalization ID
 *
 * @function
 */
const createPersonalization = async (
  listID: string,
  personalizationID: string,
  personalizedState: CreatePersonalizationInfo
): Promise<AxiosResponse<string>> => {
  const { restApi, reqType } = resolveRestApi(createListPersonalization, {
    listID
  });

  updatePersonalizationStore(CREATE, listID, personalizationID, personalizedState);

  return axiosFetch(restApi, {
    method: reqType,
    body: {
      ...personalizedState,
      personalizationState: JSON.stringify(personalizedState.personalizationState)
    }
  }).then((response: AxiosResponse<string>) => {
    const key = response.data;
    tempPersonalizationKeyHack[personalizationID] = key;
    return response;
  });
};

/**
 * This API updates a personalization instance of a list component and returns the status as a Promise.
 * @param listID - A unique ID referencing a list component. The length of the ID should be limited to 32 characters.
 * @param personalizationID - A unique id referencing the personalization instance.
 * @param personalizedState - An object containing information about the personalization state.
 *
 * @example  <caption>In the example below, the API updates a personalization instance of a list component and returns the status.</caption>
 *
 * const listId = "443533r555";
 * const personalizationId = "L_343456";
 * const personalizedState = {
 *   name : "Resolved bugs",
 *   markAsDefault : false,
 *   personalizationState : "{ filter : 'pyStatusWork STARTS_WITH Resolved'}"
 * };
 * PCore.getPersonalizationUtils(listId).updatePersonalization(personalizationId, personalizedState);
 *
 * @returns - Success
 *
 * @function
 */
const updatePersonalization = async (
  listID: string,
  personalizationID: string,
  personalizedState: CreatePersonalizationInfo
): Promise<AxiosResponse<string>> => {
  const personalizationKey = tempPersonalizationKeyHack[personalizationID] || personalizationID;

  updatePersonalizationStore(UPDATE, listID, personalizationID, personalizedState);

  const { restApi, reqType } =
    resolveRestApi(updateListPersonalization, {
      listID,
      personalizationID: personalizationKey
    }) || {};

  return axiosFetch(restApi, {
    method: reqType,
    body: {
      ...personalizedState,
      personalizationState: JSON.stringify(personalizedState.personalizationState)
    }
  });
};

/**
 * This API deletes a personalization instance of a list component and returns the status as a Promise.
 * @param listID - A unique id referencing a list component. It should not be more than 32 characters.
 * @param personalizationID - A unique id referencing the personalization instance.
 *
 * @example  <caption>In the example below, the API deletes personalization instance and returns the status.</caption>
 *
 * const listId = "443533r555";
 * const personalizationId = "L_343456";
 * PCore.getPersonalizationUtils(listID).deletePersonalization(personalizationId);
 *
 * @returns - Success
 *
 * @function
 */
const deletePersonalization = async (listID: string, personalizationID: string): Promise<AxiosResponse<string>> => {
  const personalizationKey = tempPersonalizationKeyHack[personalizationID] || personalizationID;

  updatePersonalizationStore(DELETE, listID, personalizationID);

  const { restApi, reqType } = resolveRestApi(deleteListPersonalization, {
    listID,
    personalizationID: personalizationKey
  });

  return axiosFetch(restApi, {
    method: reqType,
    body: {}
  }).then((response: AxiosResponse<string>) => {
    if (tempPersonalizationKeyHack[personalizationID]) {
      delete tempPersonalizationKeyHack[personalizationID];
    }
    return response;
  });
};

/**
 * This is the personalization utility.
 * @ignore
 * @param listID - personalization list
 */
function getPersonalizationUtils(listID: string) {
  if (!listID) {
    return {
      createPersonalization: () => Promise.resolve(''),
      updatePersonalization: () => Promise.resolve(),
      deletePersonalization: () => Promise.resolve(),
      fetchPersonalizations: () => Promise.resolve({})
    };
  }
  return {
    createPersonalization: createPersonalization.bind(null, listID),
    updatePersonalization: updatePersonalization.bind(null, listID),
    deletePersonalization: deletePersonalization.bind(null, listID),
    fetchPersonalizations: fetchPersonalizations.bind(null, listID)
  };
}

export default getPersonalizationUtils;
